import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ImagePlus, Pencil, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { StorefrontSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  QueryState,
  Section,
  Switch,
  SwitchField,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  createStorefrontBanner,
  deleteStorefrontBanner,
  fetchStorefrontBanners,
  removeStorefrontBannerMobileImage,
  reorderStorefrontBanners,
  updateStorefrontBanner,
  uploadStorefrontBannerImage,
  type StorefrontBannerView,
} from '../lib/api.ts'

/**
 * O banner da loja online (F269): o carrossel do alto do site.
 *
 * Cada banner tem a imagem do computador (larga) e, se quiser, a do celular
 * (em pé); sem a do celular, o site usa a do computador. O link leva para uma
 * página da loja (`/shop/…`) ou para fora (`https://`). O período põe e tira o
 * banner do ar sozinho.
 */

const errorText = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

/** `2026-11-28T00:00` (o campo do navegador) ↔ ISO com fuso. */
const toLocalInput = (iso: string | null): string => {
  if (iso === null) return ''
  const date = new Date(iso)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
const fromLocalInput = (value: string): string | null =>
  value === '' ? null : new Date(value).toISOString()

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

const periodText = (banner: StorefrontBannerView): string | null => {
  if (banner.startsAt === null && banner.endsAt === null) return null
  if (banner.startsAt !== null && banner.endsAt !== null) {
    return `${shortDate(banner.startsAt)} até ${shortDate(banner.endsAt)}`
  }
  return banner.startsAt === null
    ? `até ${shortDate(banner.endsAt ?? '')}`
    : `a partir de ${shortDate(banner.startsAt)}`
}

const stateOf = (
  banner: StorefrontBannerView
): { label: string; tone: 'success' | 'neutral' | 'warning' } => {
  if (banner.live) return { label: 'No ar', tone: 'success' }
  if (banner.desktop === null) return { label: 'Sem imagem', tone: 'warning' }
  if (!banner.active) return { label: 'Desligado', tone: 'neutral' }
  return { label: 'Fora do período', tone: 'neutral' }
}

export const StorefrontBanners = ({
  store,
}: {
  store: StorefrontSummary
}): ReactNode => {
  const queryClient = useQueryClient()
  const key = ['storefront-banners', store.id]
  const banners = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) =>
      await fetchStorefrontBanners(store.id, signal),
  })
  const [editing, setEditing] = useState<StorefrontBannerView | 'new' | null>(
    null
  )
  const [removing, setRemoving] = useState<StorefrontBannerView | null>(null)

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const toggle = useMutation({
    mutationFn: async (banner: StorefrontBannerView) =>
      await updateStorefrontBanner(store.id, banner.id, {
        title: banner.title,
        linkUrl: banner.linkUrl,
        startsAt: banner.startsAt,
        endsAt: banner.endsAt,
        active: !banner.active,
        version: banner.version,
      }),
    onSuccess: refresh,
    onError: (cause) => {
      notify.error(errorText(cause, 'Não foi possível mudar o banner.'))
    },
  })

  const move = useMutation({
    mutationFn: async (ids: string[]) =>
      await reorderStorefrontBanners(store.id, ids),
    onSuccess: refresh,
    onError: (cause) => {
      notify.error(errorText(cause, 'Não foi possível mudar a ordem.'))
    },
  })

  const remove = useMutation({
    mutationFn: async (banner: StorefrontBannerView) => {
      await deleteStorefrontBanner(store.id, banner.id)
    },
    onSuccess: async () => {
      setRemoving(null)
      await refresh()
    },
    onError: (cause) => {
      notify.error(errorText(cause, 'Não foi possível apagar o banner.'))
    },
  })

  const rows = banners.data ?? []
  const swap = (index: number, delta: number): void => {
    const ids = rows.map((banner) => banner.id)
    const target = index + delta
    const moving = ids[index]
    const other = ids[target]
    if (moving === undefined || other === undefined) return
    ids[index] = other
    ids[target] = moving
    move.mutate(ids)
  }

  return (
    <Section
      title="Banners"
      description="O carrossel do alto da loja. Aparece antes de o lojista entrar, na ordem desta lista."
      action={
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            setEditing('new')
          }}
        >
          <ImagePlus aria-hidden="true" /> Novo banner
        </Button>
      }
    >
      <QueryState
        isPending={banners.isPending}
        isError={banners.isError}
        error={banners.error}
        onRetry={() => void banners.refetch()}
        what="os banners"
        isEmpty={rows.length === 0}
        empty={
          <EmptyState
            icon={<ImagePlus aria-hidden="true" className="h-8 w-8" />}
            title="Nenhum banner"
            description="Sem banner, a loja abre direto nos produtos."
          />
        }
      >
        <ul className="flex flex-col gap-2">
          {rows.map((banner, index) => {
            const state = stateOf(banner)
            const period = periodText(banner)
            return (
              <li
                key={banner.id}
                className="flex flex-wrap items-center gap-3 border-l-4 border-(--border-default) py-2 pl-3"
              >
                <div className="h-14 w-40 shrink-0 overflow-hidden rounded-(--radius-sm) bg-(--surface-sunken)">
                  {banner.desktop === null ? null : (
                    <img
                      src={banner.desktop.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="flex min-w-48 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2 font-(--weight-medium)">
                    {banner.title}
                    <Badge tone={state.tone}>{state.label}</Badge>
                  </span>
                  <span className="text-(length:--text-xs) text-(--text-secondary)">
                    {[
                      period,
                      banner.linkUrl === null
                        ? 'sem link'
                        : `link: ${banner.linkUrl}`,
                      banner.mobile === null
                        ? 'celular usa a do computador'
                        : null,
                    ]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    aria-label={`${banner.title} ligado`}
                    checked={banner.active}
                    disabled={toggle.isPending}
                    onCheckedChange={() => {
                      toggle.mutate(banner)
                    }}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Subir ${banner.title}`}
                    disabled={index === 0 || move.isPending}
                    onClick={() => {
                      swap(index, -1)
                    }}
                  >
                    <ArrowUp aria-hidden="true" className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Descer ${banner.title}`}
                    disabled={index === rows.length - 1 || move.isPending}
                    onClick={() => {
                      swap(index, 1)
                    }}
                  >
                    <ArrowDown aria-hidden="true" className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Editar ${banner.title}`}
                    onClick={() => {
                      setEditing(banner)
                    }}
                  >
                    <Pencil aria-hidden="true" className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Apagar ${banner.title}`}
                    onClick={() => {
                      setRemoving(banner)
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </QueryState>

      {editing === null ? null : (
        <BannerDialog
          storeId={store.id}
          banner={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
          }}
          onSaved={refresh}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        title="Apagar o banner?"
        description={
          removing === null
            ? ''
            : `"${removing.title}" sai da loja e as imagens são apagadas.`
        }
        confirmLabel="Apagar"
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing)
        }}
      />
    </Section>
  )
}

const ImagePicker = ({
  label,
  hint,
  current,
  file,
  onPick,
  onClear,
}: {
  label: string
  hint: string
  current: string | null
  file: File | null
  onPick: (file: File) => void
  onClear?: (() => void) | undefined
}): ReactNode => {
  const preview = file === null ? current : URL.createObjectURL(file)
  return (
    <Field label={label} hint={hint}>
      {(field) => (
        <div className="flex flex-col gap-2">
          {preview === null ? null : (
            <img
              src={preview}
              alt=""
              className="max-h-40 w-full rounded-(--radius-sm) object-contain"
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              id={field.id}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              className="text-(length:--text-sm)"
              onChange={(event) => {
                const picked = event.target.files?.[0]
                if (picked !== undefined) onPick(picked)
              }}
            />
            {onClear === undefined || preview === null ? null : (
              <Button size="sm" variant="ghost" onClick={onClear}>
                Tirar
              </Button>
            )}
          </div>
        </div>
      )}
    </Field>
  )
}

const BannerDialog = ({
  storeId,
  banner,
  onClose,
  onSaved,
}: {
  storeId: string
  banner: StorefrontBannerView | null
  onClose: () => void
  onSaved: () => Promise<void>
}): ReactNode => {
  const [title, setTitle] = useState(banner?.title ?? '')
  const [link, setLink] = useState(banner?.linkUrl ?? '')
  const [startsAt, setStartsAt] = useState(
    toLocalInput(banner?.startsAt ?? null)
  )
  const [endsAt, setEndsAt] = useState(toLocalInput(banner?.endsAt ?? null))
  const [active, setActive] = useState(banner?.active ?? true)
  const [desktop, setDesktop] = useState<File | null>(null)
  const [mobile, setMobile] = useState<File | null>(null)
  const [dropMobile, setDropMobile] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const fields = {
        title: title.trim(),
        linkUrl: link.trim() === '' ? null : link.trim(),
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
        active,
      }
      const saved =
        banner === null
          ? await createStorefrontBanner(storeId, fields)
          : await updateStorefrontBanner(storeId, banner.id, {
              ...fields,
              version: banner.version,
            })
      if (desktop !== null) {
        await uploadStorefrontBannerImage(storeId, saved.id, 'desktop', desktop)
      }
      if (mobile !== null) {
        await uploadStorefrontBannerImage(storeId, saved.id, 'mobile', mobile)
      } else if (dropMobile && banner?.mobile !== null) {
        await removeStorefrontBannerMobileImage(storeId, saved.id)
      }
    },
    onSuccess: async () => {
      await onSaved()
      onClose()
    },
    onError: async (cause) => {
      setError(errorText(cause, 'Não foi possível salvar o banner.'))
      // O banner pode ter sido criado antes de a imagem falhar.
      await onSaved()
    },
  })

  const needsDesktop = banner?.desktop == null && desktop === null

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={banner === null ? 'Novo banner' : 'Editar banner'}
        description="A imagem do computador é obrigatória para o banner ir ao ar."
        className="max-w-2xl"
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            save.mutate()
          }}
        >
          <Field
            label="Nome"
            hint="Só para você; o site usa como texto alternativo."
          >
            {(field) => (
              <Input
                {...field}
                value={title}
                maxLength={120}
                onChange={(event) => {
                  setTitle(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Link ao clicar"
            hint="Uma página da loja, como /shop/categoria?nome=Fones e Headset, ou um endereço https://. Vazio: o banner não é clicável."
          >
            {(field) => (
              <Input
                {...field}
                value={link}
                onChange={(event) => {
                  setLink(event.target.value)
                }}
              />
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field label="Entra no ar em" className="w-56">
              {(field) => (
                <Input
                  {...field}
                  type="datetime-local"
                  value={startsAt}
                  onChange={(event) => {
                    setStartsAt(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Sai do ar em" className="w-56">
              {(field) => (
                <Input
                  {...field}
                  type="datetime-local"
                  value={endsAt}
                  onChange={(event) => {
                    setEndsAt(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>
          <SwitchField
            label="Ligado"
            description="Desligado, não aparece mesmo dentro do período."
            checked={active}
            onCheckedChange={setActive}
          />
          <ImagePicker
            label="Imagem do computador"
            hint="Larga: 1920 × 600 px fica bom."
            current={banner?.desktop?.url ?? null}
            file={desktop}
            onPick={setDesktop}
          />
          <ImagePicker
            label="Imagem do celular (opcional)"
            hint="Em pé ou quadrada: 1080 × 1080 px. Sem ela, o celular mostra a do computador."
            current={dropMobile ? null : (banner?.mobile?.url ?? null)}
            file={mobile}
            onPick={(file) => {
              setMobile(file)
              setDropMobile(false)
            }}
            onClear={() => {
              setMobile(null)
              setDropMobile(true)
            }}
          />
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={title.trim() === '' || needsDesktop}
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
